/* eslint-disable max-lines-per-function */
import React, { useRef, useImperativeHandle, useEffect, forwardRef } from 'react';
import cn from 'classnames';
import { useUID } from 'react-uid';
import CBForm from '@cb/apricot/CBForm';
import CBDropdown from '@cb/apricot/CBDropdown';
import SelectDropdownItem from './SelectDropdownItem';
import SelectDropdownGroup from './SelectDropdownGroup';
export const SelectDropdown = forwardRef(({ selectId, validationMessageId, mainClassName, classNameMenu, defaultValue, value = defaultValue, values, label, labelId, labelClassName, ariaLabel, ariaDescribedby, floating, floatingBg, condensed, required, disabled, reset, validation, validationMsg, validationMsgRole, helperClassName, ariaLive, iconLeft = false, onChange, onFocus, onBlur, cbRef, }, ref) => {
    const CBSelectDropdownRef = useRef(null);
    const selectDivRef = useRef(null);
    const selectButtonRef = useRef(null);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    selectId = selectId || `apricot_select_${uid}`;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_select_err_${uid}`;
    const dropdownMenuId = `apricot_select_menu_${uid}`;
    const selectClass = cn('cb-select', disabled && 'cb-disabled', floating && 'cb-floating-label', condensed && 'cb-condensed', validation && 'display-block', validation && `cb-validation-${validation}`, mainClassName);
    const labelClass = cn(floating && floatingBg && `cb-${floatingBg}-bg`, required && 'cb-required', labelClassName);
    const helperClass = cn('cb-input-helper', helperClassName, validation && `cb-validation-${validation}`);
    const getDescribedBy = () => {
        if (validation) {
            if (validationMsg && ariaDescribedby) {
                return `${ariaDescribedby} ${validationMessageId}`;
            }
            return validationMsg ? validationMessageId : ariaDescribedby;
        }
        return ariaDescribedby;
    };
    const describedbyValue = getDescribedBy() || null;
    /**
     * Render Regular options for select dropdown
     * @param options
     */
    const renderOptions = (options) => {
        return options.map((option, index) => {
            return (React.createElement(SelectDropdownItem, { key: index, label: option.label, value: option.value, disabled: option.disabled, divider: option.divider, icon: option.icon, iconLeft: true }));
        });
    };
    /**
     * Render grouped options for select dropdown
     * @param groupedOptions
     */
    const renderGroupOptions = (groupedOptions) => {
        return groupedOptions.map((options, index) => {
            return (React.createElement(SelectDropdownGroup, { key: index, optgroup: options.optgroup }, options.options && renderOptions(options.options)));
        });
    };
    const onSelectChange = (selValue, selLabel) => {
        // Handle the change event here
        onChange && onChange(selValue, selLabel);
    };
    useImperativeHandle(ref, () => selectButtonRef.current);
    const isCalledRef = useRef(false);
    useEffect(() => {
        floating && CBForm.floatingLabel(selectButtonRef.current);
        const selectDropdownPlugin = CBDropdown({
            elem: selectDivRef.current,
            selectDropdown: true,
            closeOnClick: true,
            defaultValue,
            callBack: onSelectChange,
        });
        if (cbRef) {
            // eslint-disable-next-line no-param-reassign
            cbRef.current = selectDropdownPlugin;
        }
        CBSelectDropdownRef.current = selectDropdownPlugin;
        return () => {
            // We just want this to run once
            if (isCalledRef.current) {
                selectDropdownPlugin === null || selectDropdownPlugin === void 0 ? void 0 : selectDropdownPlugin.destroy();
            }
            else {
                isCalledRef.current = true;
            }
        };
    }, []);
    // make sure latest data has all the options
    useEffect(() => {
        CBSelectDropdownRef.current && CBSelectDropdownRef.current.selectDropdownInit();
    }, [values]);
    useEffect(() => {
        CBSelectDropdownRef.current && CBSelectDropdownRef.current.setValue(value);
    }, [value]);
    useEffect(() => {
        if (reset) {
            CBSelectDropdownRef.current && CBSelectDropdownRef.current.reset();
        }
    }, [reset]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: selectClass, ref: selectDivRef, "data-cbtrack-comp": "apricot-react:select-dropdown" },
            label && (React.createElement("label", { htmlFor: selectId, className: labelClass, id: labelId }, label)),
            React.createElement("div", { className: "cb-dropdown cb-dropdown-select" },
                React.createElement("button", { ref: selectButtonRef, type: "button", className: "cb-dropdown-toggle", id: selectId, role: "combobox", "aria-expanded": "false", "aria-haspopup": "listbox", "aria-controls": dropdownMenuId, "aria-label": ariaLabel, "aria-labelledby": labelId, "aria-invalid": validation === 'error', "aria-describedby": describedbyValue || undefined, "aria-required": required, "aria-disabled": disabled, tabIndex: disabled ? -1 : undefined, onFocus: (event) => {
                        onFocus && onFocus(event);
                    }, onBlur: (event) => {
                        onBlur && onBlur(event);
                    } },
                    React.createElement("span", { className: "cb-select-dropdown-value" }),
                    React.createElement("i", { className: "cb-icon cb-down", "aria-hidden": "true" }),
                    React.createElement("i", { className: "cb-icon cb-up cb-hidden", "aria-hidden": "true" })),
                React.createElement("div", { className: cn('cb-dropdown-menu', classNameMenu) },
                    React.createElement("ul", { role: "listbox", "aria-labelledby": labelId, id: dropdownMenuId, className: cn(iconLeft && 'cb-dropdown-left-icon') }, (values === null || values === void 0 ? void 0 : values.some((item) => item.hasOwnProperty('optgroup')))
                        ? renderGroupOptions(values)
                        : renderOptions(values))))),
        validation && validationMsg && (React.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, validationMsg))));
});
//# sourceMappingURL=SelectDropdown.js.map